import { mapState } from 'vuex';
import axios from 'axios';

// @vue/component
import { redirectToApp, redirectToAppWithError } from '@/utils/appLoginHelper';
import { handleAPIError, ERROR_TYPE } from '@/utils/errorHelper';
import { smartFocus } from '@/utils/uiHelper';
import { fixProtocol } from '@/utils/urlHelper';

import PageHeader from '@/components/widgets/PageHeader/PageHeader.vue';
import AppConsent from '@/components/sections/AppConsent/AppConsent.vue';
import GDPRConsent from '@/components/sections/GDPRConsent/GDPRConsent.vue';
import CompanyInfo from '@/components/sections/Login/CompanyInfo.vue';
import Loading from '@/components/widgets/Loading/Loading.vue';
import PersonaCapture from '@/components/sections/PersonaCapture/PersonaCapture.vue';

export default {
  name: 'TokenAuth',
  components: {
    PageHeader,
    Loading,
    AppConsent,
    'gdpr-consent': GDPRConsent,
    CompanyInfo,
    PersonaCapture,
  },
  data() {
    return {
      code: '',
      app: {},
      gdprSeen: false,
      companyInfo: undefined,
      onCompanyInfoSaved: undefined,
      onPersonaInfoSaved: undefined,

      state: 'normal',
      isLoading: true,
    };
  },
  created() {
    this.code = this.$route.query.code;

    if (this.inAppLogin) {
      this.getAppInfo();
    } else {
      // token login
      this.loginViaToken();
    }
  },
  computed: {
    ...mapState({
      afterLoginURL: (state) => {
        let baseURL = '';
        if (state.login.loginType === 'account') {
          baseURL = window.location.href.replace(/(\/launchpad\/tokenauth)\/?/, '/');
        }
        return state.login.afterLoginURL || baseURL;
      },
      // eslint-disable-next-line
      title: (state) => state.site.siteInfo.displayOptions.showSiteNameOnLoginScreen ? state.site.siteInfo.name : 'Log in',
      loginType: (state) => state.login.loginType,
      loginState: (state) => state.login.loginState,
      appInfo: (state) => state.login.appInfo,
      inAppLogin: (state) => state.login.loginType === 'app',
    }),
  },
  methods: {
    getAppInfo() {
      this.isLoading = true;
      const payload = {
        ...this.appInfo,
        code: this.code,
      };

      axios.post('/launchpad/v1/apps/validate.json', payload).then((response) => {
        this.app = response.data.app;

        if (this.app.consentRequired === false) { // allow skipping screen for our own apps
          axios.post('/launchpad/v1/token/consent.json', { code: this.code }).then((res) => {
            redirectToApp(this.appInfo, res.data.code);
          }).catch(() => {
            redirectToAppWithError(this.appInfo, 'failed_to_consent', 'impossible to consent');
          });
        } else {
          this.state = 'app-consent';
        }

        this.isLoading = false;
        smartFocus();
      }).catch((e) => {
        handleAPIError(this.$store, e, 'Failed to log in.');
        this.isLoading = false;
        smartFocus();
      });
    },
    loginViaToken() {
      this.isLoading = true;

      axios.post('/launchpad/v1/token/consent.json', { code: this.code }).then((response) => {
        const data = {
          code: response.data.code,
          rememberMe: !!this.$route.query.rememberMe,
        };
        if (this.gdprSeen) data.gdprSeen = true;

        return axios.post('/launchpad/v1/token/login.json', data).then(async (res) => {
          if (!res.data.gdprSeen) {
            this.state = 'gdpr-consent';
            this.gdprSeen = true;
            this.isLoading = false;
            return;
          }

          if (res.data.setupScreenRequired) {
            const { industryCategoryId, companySizeId } = res.data;

            this.companyInfo = {
              industryCategoryId: industryCategoryId || undefined,
              sizeId: companySizeId || undefined,
            };

            const companyInfoSavedPromise = new Promise((resolve) => {
              this.onCompanyInfoSaved = resolve;
            });

            this.state = 'company-info';
            this.isLoading = false;

            await companyInfoSavedPromise;

            this.isLoading = true;
            this.state = 'normal';
          }

          if (res.data.userPersona !== undefined && !res.data.userPersona) {
            const personaInfoSavedPromise = new Promise((resolve) => {
              this.onPersonaInfoSaved = resolve;
            });
            this.state = 'persona-capture';
            this.$store.commit('setFormState', this.state);
            this.isLoading = false;

            await personaInfoSavedPromise;

            this.isLoading = true;
            this.state = 'normal';
          }

          this.$store.commit('setLoggedIn', {
            loggedIn: true,
            userId: res.data.userId,
          });

          if (window.opener &&
            this.$store.state.login.meta.hasLocalStorage &&
            sessionStorage.getItem('shouldCloseOpener') === 'true'
          ) {
            sessionStorage.setItem('shouldCloseOpener', 'false');
            window.close();
          }

          // eslint-disable-next-line
          window.location.href = this.afterLoginURL || window.location.href.replace(/(\/launchpad\/tokenauth)\/?/, '/');
        });
      }).catch((e) => {
        const err = handleAPIError(this.$store, e, 'Failed to log in.');

        if (err && err.type === ERROR_TYPE.INSTALLATION_PROVISIONING) {
          this.$store.commit('showFatalError', {
            error: 'Please Wait',
            message: 'Your account is being set up, we\'re just wrapping up a few bits...',
            explanation: 'This should only take a moment. ' +
              'Once everything is set up, you will be logged in automatically. ' +
              'If the installation is not active after 5 minutes, please contact support.',
          });

          // This would call 'loginViaToken' in 'created' because component appears again
          setTimeout(() => this.$store.commit('resetFatalError'), 3000);

          return;
        }

        if (err && err.type === ERROR_TYPE.INSTALLATION_VALIDATION_PENDING) {
          const isVerifyEmail = this.$route.query.continue && this.$route.query.continue.includes('verifyemail');

          if (isVerifyEmail) {
            window.location.href = fixProtocol(
              `${this.$route.query.continue}?auth=${e.response.data.unvalidatedAccountLoginCode}&email=${encodeURIComponent(this.email)}`,
            );
          } else {
            window.location.href = fixProtocol(
              `/launchpad/verifyemail/${
                this.$route.params.productCode ? this.$route.params.productCode : 'projects'
              }/firstsend?auth=${e.response.data.unvalidatedAccountLoginCode}&email=${encodeURIComponent(this.email)}`,
            );
          }
        }

        // most likely, the token has expired, show fatal anyway
        if (err && err.type === ERROR_TYPE.FORM_ERROR) {
          this.$store.commit('showFatalError', {
            error: 'Login Failed',
            message: 'Login token has expired, please try again.',
            appError: 'access_denied',
            appErrorExplanation: 'google sso access token expired',
          });
        }

        this.isLoading = false;
      });
    },
    reload() {
      window.location.reload();
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/pages/TokenAuth/TokenAuth.js